import { pick } from "@nosto/search-js/utils"
import Product from "@/components/Product/DynamicCardProduct"
import { useDecoratedSearchResults, useNostoAppState } from "@nosto/search-js/preact/hooks"
import style from "./Products.module.css"
import { defaultSize, hitDecorators } from "@/config"
import { useEffect, useMemo, useRef, useState } from "preact/hooks"
import { readInGridConfig, type InGridConfig } from "@/utils/ingridConfig"
import InGridImageTile from "@/components/InGridTile/InGridImageTile"
import InGridCopyTile from "@/components/InGridTile/InGridCopyTile"
import InGridVideoTile from "@/components/InGridTile/InGridVideoTile"
import ProductCardSkeleton from "@/components/Product/ProductCardSkeleton"

type InterleaveItem<T> = { kind: "product"; data: T } | { kind: "ingrid"; data: InGridConfig }

function getGridColumns(viewportWidth: number): number {
  if (viewportWidth < 375) return 1
  if (viewportWidth < 768) return 2
  if (viewportWidth < 1024) return 3
  return 4
}

function getTileSpan(config: InGridConfig, viewportWidth: number, columns: number): number {
  const rawSpan = viewportWidth <= 767 ? config.mobileWidth : config.desktopWidth
  return Math.max(1, Math.min(rawSpan, columns))
}

function normalizeSlotForSpan(position: number, span: number, columns: number): number {
  if (span <= 1 || columns <= 1) return position
  const col = ((position - 1) % columns) + 1
  if (col + span - 1 <= columns) return position
  return position + (columns - col + 1)
}

function getInsertIndex<T>(
  items: InterleaveItem<T>[],
  targetSlot: number,
  viewportWidth: number,
  columns: number
): number {
  let slot = 1

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    const span = item.kind === "ingrid" ? getTileSpan(item.data, viewportWidth, columns) : 1
    const end = slot + span - 1

    if (targetSlot <= slot) return i
    if (targetSlot <= end) return i + 1

    slot = end + 1
  }

  return items.length
}

function interleaveItems<T>(hits: T[], configs: InGridConfig[], viewportWidth: number): InterleaveItem<T>[] {
  const items: InterleaveItem<T>[] = hits.map(p => ({ kind: "product" as const, data: p }))
  const sorted = [...configs].sort((a, b) => a.position - b.position)
  const columns = getGridColumns(viewportWidth)

  for (const config of sorted) {
    const span = getTileSpan(config, viewportWidth, columns)
    const requestedSlot = Math.max(1, config.position)
    const normalizedSlot = normalizeSlotForSpan(requestedSlot, span, columns)
    const insertAt = getInsertIndex(items, normalizedSlot, viewportWidth, columns)
    items.splice(insertAt, 0, { kind: "ingrid" as const, data: config })
  }
  return items
}

export default function Products() {
  const { loading, query } = useNostoAppState(state => pick(state, "loading", "query"))
  const { products } = useDecoratedSearchResults<typeof hitDecorators>()
  const inGridConfigs = useMemo(() => readInGridConfig(), [])
  const containerRef = useRef<HTMLDivElement>(null)
  const [productCardHeight, setProductCardHeight] = useState<number | null>(null)
  const [productGridHeight, setProductGridHeight] = useState<number | null>(null)

  const hits = products?.hits
  const hasProducts = hits && hits.length > 0
  const showLoadingSkeletons = loading && !hasProducts
  const viewportWidth = typeof window === "undefined" ? 1024 : window.innerWidth
  const isFirstPage = !query?.products?.from
  const hitCount = hits?.length ?? 0
  const showTiles = hasProducts && !loading
  const activeConfigs = inGridConfigs.filter(c => (isFirstPage || c.showOnAllPages) && c.position <= hitCount)

  useEffect(() => {
    const container = containerRef.current
    if (!container || !hasProducts || loading) return

    let frame = 0
    let cardResizeObserver: ResizeObserver | null = null

    const measure = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const card = container.querySelector("nosto-dynamic-card") as HTMLElement | null
        if (!card) return

        const nextHeight = Math.round(card.getBoundingClientRect().height)
        if (nextHeight > 0) {
          setProductCardHeight(prev => (prev === nextHeight ? prev : nextHeight))
        }

        const nextGridHeight = Math.round(container.getBoundingClientRect().height)
        if (nextGridHeight > 0) {
          setProductGridHeight(prev => (prev === nextGridHeight ? prev : nextGridHeight))
        }

        if (!cardResizeObserver) {
          cardResizeObserver = new ResizeObserver(() => measure())
          cardResizeObserver.observe(card)
        }
      })
    }

    const mutationObserver = new MutationObserver(() => measure())
    mutationObserver.observe(container, { childList: true, subtree: true })

    const containerResizeObserver = new ResizeObserver(() => measure())
    containerResizeObserver.observe(container)

    measure()

    return () => {
      cancelAnimationFrame(frame)
      mutationObserver.disconnect()
      containerResizeObserver.disconnect()
      cardResizeObserver?.disconnect()
    }
  }, [hasProducts, hitCount, loading])

  const containerStyle = {
    ...(productCardHeight ? { "--ns-product-card-height": `${productCardHeight}px` } : {}),
    ...(loading && productGridHeight ? { "--ns-products-grid-min-height": `${productGridHeight}px` } : {})
  } as Record<string, string>

  return (
    <div ref={containerRef} className={style.container} data-loading={loading} style={containerStyle}>
      {showLoadingSkeletons &&
        Array.from({ length: query?.products?.size ?? defaultSize }, (_, index) => (
          <ProductCardSkeleton key={`loading-skeleton-${index}`} />
        ))}
      {!showLoadingSkeletons &&
        hasProducts &&
        interleaveItems(hits, showTiles ? activeConfigs : [], viewportWidth).map((item, index) => {
          if (item.kind === "ingrid") {
            switch (item.data.type) {
              case "image_ingrid":
                return <InGridImageTile config={item.data} key={`ingrid-${item.data.position}`} />
              case "copy_block_ingrid":
                return <InGridCopyTile config={item.data} key={`ingrid-${item.data.position}`} />
              case "video_ingrid":
                return <InGridVideoTile config={item.data} key={`ingrid-${item.data.position}`} />
            }
          }
          return <Product product={item.data} key={item.data.productId || index} />
        })}
    </div>
  )
}
